/*
 * lfx-pick.js — "the design follows you"
 * WEBMASTER, 10 Sept 2026. Go-live runbook step 1.8.
 *
 * ONE pick at a time, held in localStorage under `lfx.pick`, shown in a bar on every page, and
 * carried onto the add-to-cart form as line properties so it reaches the workshop.
 *
 * ORDER OF RESOLUTION IS URL FIRST, THEN STORAGE. A shopper who lands with ?design=<handle> gets
 * that design even if they were holding another, because the link they followed is a more recent
 * intent than the thing in their browser. The URL is also the ONLY channel that survives a private
 * window with storage blocked, which is why every wall link carries it.
 *
 * TRAP: every storage call is wrapped. Safari in private mode throws on setItem rather than
 * returning null, and an unguarded write there takes the whole wall down with it. `hasStore` is
 * measured with a real round-trip, not assumed from the presence of window.localStorage.
 *
 * TRAP: add-to-cart NEVER clears the pick. Owner ruling — a shopper buying two hats with the same
 * design should not have to pick it twice.
 *
 * TRAP: the theme's `.installment` form also posts to /cart/add. Properties go on
 * `form.product-form` only, or Shop Pay instalments get a bogus line item property.
 */
(function () {
  'use strict';

  var KEY = 'lfx.pick';
  var VER = 1;
  var BROWSE_LABEL = 'Browse the Design Library';
  var listeners = [];
  var current = null;

  /* ---------------------------------------------------------------- storage */

  var hasStore = (function () {
    try {
      var k = '__lfx_probe__';
      window.localStorage.setItem(k, '1');
      window.localStorage.removeItem(k);
      return true;
    } catch (e) {
      return false;
    }
  })();

  function readStore() {
    if (!hasStore) return null;
    try {
      var raw = window.localStorage.getItem(KEY);
      if (!raw) return null;
      var o = JSON.parse(raw);
      return o && o.h ? o : null;
    } catch (e) {
      return null;
    }
  }

  function writeStore(p) {
    if (!hasStore) return false;
    try {
      window.localStorage.setItem(KEY, JSON.stringify(p));
      return true;
    } catch (e) {
      return false;
    }
  }

  function clearStore() {
    if (!hasStore) return;
    try { window.localStorage.removeItem(KEY); } catch (e) {}
  }

  /* ------------------------------------------------------------------- util */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* A handle is all we get from a URL-only arrival — no bundle is loaded off the wall, so there is
     nothing to resolve a name against. Title-case the handle so the bar says something a person
     recognises instead of showing raw slug text. */
  function nameFromHandle(h) {
    if (/^ad\d{3,4}$/i.test(h)) return 'After Dark design';
    return String(h)
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b[a-z]/g, function (c) { return c.toUpperCase(); });
  }

  /* The wall's sheets come in pairs by name: lfx-sheetc<v>-<n>.jpg (colour) and lfx-sheetm<v>-<n>.png
     (alpha mask). Until the wall passes the mask explicitly, derive it from the colour sheet's URL. */
  function maskFor(bg) {
    var m = /^(.*\/)lfx-sheetc(\d+)-(\d+)\.jpg(\?.*)?$/.exec(bg);
    return m ? m[1] + 'lfx-sheetm' + m[2] + '-' + m[3] + '.png' : '';
  }

  function normalise(p) {
    if (!p || !p.h) return null;
    return {
      v: VER,
      h: String(p.h).trim(),
      n: p.n ? String(p.n).trim() : nameFromHandle(p.h),
      img: p.img ? String(p.img) : '',
      /* Sprite coordinates, for a design whose art is a cell in a sheet rather than its own file.
         `img` stays the simple case; `sp` is {bg, bgs, bgp} straight off the wall tile. Without
         this every Design Library pick showed the empty placeholder square, because a sheet cell
         has no URL of its own. */
      sp: (p.sp && p.sp.bg) ? { bg: String(p.sp.bg), bgs: String(p.sp.bgs || 'contain'), bgp: String(p.sp.bgp || 'center'), mask: p.sp.mask ? String(p.sp.mask) : maskFor(String(p.sp.bg)) } : null,
      pw: (p.pw && +p.pw > 0) ? +p.pw : 3,
      /* 16 Sept (owner: "a nested hat"): a design whose art is already a hat photo or a product
         mockup is never drawn onto a cap again. The wall sets nm (no-mock) on such picks. */
      nm: !!p.nm,
      ad: !!p.ad,
      t: p.t || Date.now()
    };
  }

  function fromUrl() {
    var q;
    try { q = new URLSearchParams(window.location.search); } catch (e) { return null; }
    var h = q.get('design') || q.get('d');
    if (!h) return null;
    return normalise({ h: h, ad: /^ad\d{3,4}$/i.test(h) });
  }

  function emit() {
    for (var i = 0; i < listeners.length; i++) {
      try { listeners[i](current); } catch (e) {}
    }
    try {
      document.dispatchEvent(new CustomEvent('lfx:pick', { detail: current }));
    } catch (e) {}
  }

  /* Renders whichever art the pick actually has: a plain image URL, a sprite-sheet cell, or a
     neutral placeholder. Returns markup, never null, so callers do not branch. */
  function thumbHTML(cls){
    if (!current) return '';
    if (current.sp && current.sp.bg){
      return '<span class="' + cls + '" aria-hidden="true" style="' + artStyle(current.sp) + '"></span>';
    }
    if (current.img){
      return '<span class="' + cls + '" aria-hidden="true" style="background-image:url(' + esc(current.img) + ')"></span>';
    }
    return '<span class="' + cls + ' ' + cls + '--none" aria-hidden="true"></span>';
  }

  /* Inline style for a sprite cell, with the sheet's alpha mask when the pick carries one (a masked
     cell is a cut-out patch; an unmasked one is the cell on its sheet background). */
  function artStyle(sp){
    var st = 'background-image:url(' + esc(sp.bg) + ');background-size:' + esc(sp.bgs) + ';background-position:' + esc(sp.bgp) + ';background-repeat:no-repeat';
    if (sp.mask) st += ';-webkit-mask-image:url(' + esc(sp.mask) + ');mask-image:url(' + esc(sp.mask) + ');-webkit-mask-size:' + esc(sp.bgs) + ';mask-size:' + esc(sp.bgs) +
      ';-webkit-mask-position:' + esc(sp.bgp) + ';mask-position:' + esc(sp.bgp) + ';-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat';
    return st;
  }

  /* The black Richardson blank the Design Library's "On a hat" view uses (LFX_DESIGNS.blanks.black):
     measured centre and pixels-per-inch, so the patch lands where it does on the wall. 11 Sept 2026. */
  var CAP_BLACK = { uri: 'https://cdn.shopify.com/s/files/1/0866/9288/8878/files/lfx-cap-black.jpg?v=1788632534', size: 520, pxIn: 47.58, cx: 261.3, cy: 228.8 };

  /* "On this hat" preview for the product page: the pick's cut-out art on the blank. Falls back to the
     plain thumb when the pick has no mask (a URL-only arrival, or an image pick). */
  function capMockHTML(cls){
    if (!current || current.nm || !current.sp || !current.sp.bg || !current.sp.mask) return thumbHTML(cls);
    var b = CAP_BLACK, pw = (current.pw || 3) * 1.1 * b.pxIn;
    var left = (b.cx - pw / 2) / b.size * 100, top = b.cy / b.size * 100, wid = pw / b.size * 100;
    return '<span class="lfx-mock ' + cls + '" aria-hidden="true"><img src="' + b.uri + '" alt="" width="520" height="520" loading="lazy">' +
      '<span class="lfx-mock__ov" style="left:' + left + '%;top:' + top + '%;width:' + wid + '%"><span class="lfx-mock__sp" style="' + artStyle(current.sp) + '"></span></span></span>';
  }

  /* --------------------------------------------------------------- the bar */

  var bar = null;

  function dockHeight() {
    var d = document.querySelector('.mobile-dock');
    /* `invisible` is visibility:hidden in this theme, so the element still reports its box. */
    return d ? d.offsetHeight : 0;
  }

  function renderBar() {
    var host = document.getElementById('lfx-pick-bar');
    if (!host) return;

    if (!current) {
      host.innerHTML = '';
      host.hidden = true;
      document.documentElement.classList.remove('lfx-has-pick');
      document.documentElement.style.removeProperty('--lfx-pick-h');
      return;
    }

    var thumb = thumbHTML('lfx-pick__thumb');

    host.innerHTML =
      '<div class="lfx-pick__in">' +
        thumb +
        '<span class="lfx-pick__txt">' +
          '<span class="lfx-pick__lbl">Your design</span>' +
          '<span class="lfx-pick__name">' + esc(current.n) + '</span>' +
        '</span>' +
        '<span class="lfx-pick__acts">' +
          '<a class="lfx-pick__go" href="/pages/shop">Choose a hat</a>' +
          '<a class="lfx-pick__alt" href="/pages/designs">Change</a>' +
          '<button type="button" class="lfx-pick__x" data-lfx-clear aria-label="Remove your design">&times;</button>' +
        '</span>' +
      '</div>';

    host.hidden = false;
    document.documentElement.classList.add('lfx-has-pick');
    document.documentElement.style.setProperty('--lfx-dock-h', dockHeight() + 'px');
    document.documentElement.style.setProperty('--lfx-pick-h', host.offsetHeight + 'px');

    var x = host.querySelector('[data-lfx-clear]');
    if (x) x.addEventListener('click', function () { api.clear(); });
  }

  /* ------------------------------------------------------- the product page */

  /* Eligibility is decided in Liquid, not guessed here — see snippets/lfx-pick.liquid. */
  function productCtx() {
    return window.__LFX_PRODUCT || null;
  }

  function productForm() {
    return document.querySelector('form.product-form[action*="/cart/add"]');
  }

  /* The slot is created here rather than in Liquid on purpose: it has to sit directly above the
     buy buttons, and that markup lives in sections/main-product.liquid — 136 KB of vendor theme
     that nothing else in this build touches. Inserting one node beside a known anchor is a much
     smaller bet than editing that file. If the anchor ever disappears the slot simply never
     mounts, the properties still go on the form, and the pick still reaches the order. */
  function ensureSlot() {
    var slot = document.getElementById('lfx-pick-onproduct');
    if (slot) return slot;

    /* 11 Sept 2026 (owner: "no way to show you selected a design"): the options app (Easify) renders
       its own "Pick Your Design" block well above the buy buttons, so the pick sits ABOVE that block
       where the shopper is actually looking. Falls back to the buy buttons if the app block is absent. */
    var anchor = document.querySelector('[id^="shopify-block-"][id*="easify_product_options"]') ||
                 document.querySelector('.buy-buttons');
    if (!anchor || !anchor.parentNode) return null;

    slot = document.createElement('div');
    slot.id = 'lfx-pick-onproduct';
    slot.hidden = true;
    anchor.parentNode.insertBefore(slot, anchor);
    return slot;
  }

  function syncProduct() {
    var ctx = productCtx();
    if (!ctx) return;

    var form = productForm();
    var slot = ensureSlot();

    /* Properties: rebuilt from scratch every time so a change of pick can never leave the old
       handle behind on the form. */
    if (form) {
      var stale = form.querySelectorAll('[data-lfx-prop]');
      for (var i = 0; i < stale.length; i++) stale[i].parentNode.removeChild(stale[i]);

      if (current && ctx.accepts) {
        form.appendChild(hidden('properties[_design_handle]', current.h));
        form.appendChild(hidden('properties[Design]', current.n));
        /* Hidden (underscore) property so the cart drawer and cart page can draw the design next to the
           line. Never shown at checkout — checkout shows the "Design: <name>" line above. */
        if (current.sp && current.sp.bg) form.appendChild(hidden('properties[_design_art]', JSON.stringify({ bg: current.sp.bg, bgs: current.sp.bgs, bgp: current.sp.bgp, mask: current.sp.mask || '' })));
      }
    }

    if (!slot) return;

    if (!current) {
      /* Brief section 6, path 2 — hat -> wall -> hat. A product page with no pick offers the door
         into the library and carries `?for=` so the wall can send the customer straight back here
         with the design applied. Without this the two halves of the catalogue only meet if the
         shopper happens to start on the wall. */
      if (ctx.accepts) {
        slot.innerHTML =
          '<p class="lfx-onprod lfx-onprod--browse">' +
            '<a class="lfx-onprod__browse" href="/pages/designs?for=' + encodeURIComponent(ctx.handle) + '">' +
            BROWSE_LABEL + '</a>' +
          '</p>';
        slot.hidden = false;
      } else {
        slot.innerHTML = '';
        slot.hidden = true;
      }
      return;
    }

    if (!ctx.accepts) {
      /* Owner ruling, 10 Sept 2026 evening: the ruled sentence says "on this hat", which is only
         true on a hat. Expedite Order Production is a Service and reads as nonsense with the hat
         wording, so the noun follows product.type. Two strings, one rule, no third variant. */
      var noun = (ctx.type === 'Hat') ? 'hat' : 'item';
      slot.innerHTML =
        '<p class="lfx-onprod lfx-onprod--no">This design isn’t available on this ' + noun + '.</p>';
      slot.hidden = false;
      return;
    }

    var thumb = capMockHTML('lfx-onprod__mock');
    slot.innerHTML =
      '<div class="lfx-onprod lfx-onprod--panel">' + thumb +
        '<span class="lfx-onprod__txt">' +
          '<span class="lfx-onprod__lbl">Your design</span>' +
          '<strong class="lfx-onprod__name">' + esc(current.n) + '</strong>' +
          '<span class="lfx-onprod__sub">Goes on this one and onto your order.</span>' +
          '<span class="lfx-onprod__acts">' +
            '<a href="/pages/designs?for=' + encodeURIComponent(ctx.handle) + '">Change design</a>' +
            '<button type="button" class="lfx-onprod__rm" data-lfx-clear-onprod>Remove</button>' +
          '</span>' +
        '</span>' +
      '</div>';
    var rm = slot.querySelector('[data-lfx-clear-onprod]');
    if (rm) rm.addEventListener('click', function () { api.clear(); });
    slot.hidden = false;
  }

  /* ----------------------------------------------------------------- the cart */

  /* Cart drawer + cart page: draw the picked design beside the "Design:" line of every item that
     carries `_design_art`. Reads /cart.js (underscore properties are not in the DOM), finds the
     item's row by its line key, and prepends one thumb. Re-runs after the drawer re-renders. */
  var cartTimer = null;
  function decorateCart() {
    if (!document.querySelector('.cart-item, [id^="CartItem-"], [id^="CartDrawer-Item"]')) return;
    fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.ok ? r.json() : null; }).then(function (cart) {
      if (!cart || !cart.items) return;
      for (var i = 0; i < cart.items.length; i++) {
        var it = cart.items[i], raw = it.properties && it.properties._design_art;
        if (!raw) continue;
        var sp; try { sp = JSON.parse(raw); } catch (e) { continue; }
        if (!sp || !sp.bg) continue;
        /* Many elements carry the line key in their id (the row, its quantity input, its remove link);
           only a row that actually holds the "Design:" property line gets the thumb, once. */
        var rows = document.querySelectorAll('[id]');
        var name = it.properties.Design || (current && current.n) || '';
        for (var r = 0; r < rows.length; r++) {
          var row = rows[r];
          if (row.id.indexOf(it.key) === -1 || row.querySelector('.lfx-cart-thumb') || row.closest('.lfx-cart-line')) continue;
          var target = null, cands = row.querySelectorAll('dd, dt, span, div, p');
          for (var c = 0; c < cands.length; c++) {
            var txt = (cands[c].textContent || '').replace(/\s+/g, ' ').trim();
            if (cands[c].children.length <= 2 && txt && (txt === name || /^Design:\s*/.test(txt)) && cands[c].closest('dl, .cart-item__details, [class*="properties"], [class*="option"]')) { target = cands[c]; break; }
          }
          if (!target) continue;
          var t = document.createElement('span');
          t.className = 'lfx-cart-thumb';
          t.setAttribute('aria-hidden', 'true');
          t.setAttribute('style', artStyle(sp));
          var line = target.closest('div, dd') || target;
          if (line.querySelector('.lfx-cart-thumb')) break;
          line.insertBefore(t, line.firstChild);
          line.classList.add('lfx-cart-line');
          break;
        }
      }
    }).catch(function () {});
  }
  function scheduleCart() { clearTimeout(cartTimer); cartTimer = setTimeout(decorateCart, 350); }
  document.addEventListener('DOMContentLoaded', scheduleCart);
  try {
    new MutationObserver(function (m) {
      for (var i = 0; i < m.length; i++) {
        var n = m[i].target;
        if (n && n.nodeType === 1 && (n.closest ? n.closest('cart-drawer, #cart-drawer, .cart-drawer, [id*="cart"], form[action="/cart"], .cart-item') : null)) { scheduleCart(); return; }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  } catch (e) {}

  function hidden(name, value) {
    var i = document.createElement('input');
    i.type = 'hidden';
    i.name = name;
    i.value = value;
    i.setAttribute('data-lfx-prop', '');
    return i;
  }

  /* ------------------------------------------------------------------- api */

  var api = {
    supported: function () { return hasStore; },

    get: function () { return current ? JSON.parse(JSON.stringify(current)) : null; },

    set: function (p) {
      var n = normalise(p);
      if (!n) return null;
      current = n;
      writeStore(n);
      renderBar();
      syncProduct();
      emit();
      return api.get();
    },

    clear: function () {
      current = null;
      clearStore();
      renderBar();
      syncProduct();
      emit();
    },

    /* Every link out of a wall goes through this, so the pick survives a browser that refuses
       storage. Without it the private-window path is broken and nothing says so. */
    decorate: function (url) {
      if (!current || !url) return url;
      try {
        var u = new URL(url, window.location.origin);
        u.searchParams.set(current.ad ? 'd' : 'design', current.h);
        return u.pathname + u.search + u.hash;
      } catch (e) {
        return url + (url.indexOf('?') === -1 ? '?' : '&') +
          (current.ad ? 'd=' : 'design=') + encodeURIComponent(current.h);
      }
    },

    onChange: function (fn) { if (typeof fn === 'function') listeners.push(fn); }
  };

  window.LFXPick = api;

  /* ------------------------------------------------------------------ boot */

  function boot() {
    var url = fromUrl();
    var stored = readStore();

    if (url && (!stored || stored.h !== url.h)) {
      /* Keep the richer stored record if the URL is just re-stating the same pick. */
      current = url;
      writeStore(url);
    } else {
      current = stored || url;
    }

    renderBar();
    syncProduct();
    emit();

    window.addEventListener('resize', function () {
      var host = document.getElementById('lfx-pick-bar');
      if (!host || host.hidden) return;
      document.documentElement.style.setProperty('--lfx-dock-h', dockHeight() + 'px');
      document.documentElement.style.setProperty('--lfx-pick-h', host.offsetHeight + 'px');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
